import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, ArrowRight, Award, Sparkles, TrendingUp, Compass, Youtube, Instagram, Facebook } from "lucide-react";
import { useLanguage } from "../components/LanguageContext";
import { FOCUS_WORDS_TRANSLATED } from "../translations";
import { CONTACT_INFO } from "../data";
import SEO from "../components/SEO";
import ImagePlaceholder from "../components/ImagePlaceholder";
import {
  DraftGrid,
  HorseLineArt,
  CornerBrackets,
  OrganicLines,
  EquestrianEmblem,
  DecorativeDivider
} from "../components/VectorDecor";

export default function Home() {
  const { lang, t } = useLanguage();

  const focusWords = FOCUS_WORDS_TRANSLATED[lang] || FOCUS_WORDS_TRANSLATED.da;

  const heroImages = [
    "https://assets.auxodev.dk/assets/47342516-e670-4852-ad73-fdec3014e752/image00005.jpeg",
    "https://assets.auxodev.dk/assets/47342516-e670-4852-ad73-fdec3014e752/image00001.jpeg",
    "https://assets.auxodev.dk/assets/47342516-e670-4852-ad73-fdec3014e752/image00002.jpeg"
  ];

  const [currentHeroIdx, setCurrentHeroIdx] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentHeroIdx((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-[#FCF9F5] text-stone-900 font-sans-custom">
      <SEO pageKey="home" />

      {/* Section 2: Hero Brand Title Section (Asymmetrical Split Layout) */}
      <section className="relative min-h-[90vh] flex items-end justify-start overflow-visible border-b border-stone-200 pb-0 pt-16 lg:pt-24 z-10">
        {/* Full Section Background Slider */}
        <div className="absolute inset-0 z-0 overflow-hidden bg-stone-950">
          {heroImages.map((img, idx) => (
            <img
              key={img}
              src={`${img}?width=1600&quality=85&format=auto`}
              alt="Equinesport Hero"
              className={`absolute inset-0 w-full h-full object-cover grayscale transition-opacity duration-1000 ease-in-out ${
                idx === currentHeroIdx ? "opacity-50 lg:opacity-40" : "opacity-0"
              }`}
            />
          ))}
          {/* Elegant gradient overlay to ensure text readability while preserving the image */}
          <div className="absolute inset-0 bg-gradient-to-r from-stone-950/60 via-stone-950/20 to-transparent lg:from-[#FCF9F5]/75 lg:via-[#FCF9F5]/30 lg:to-transparent" />
          
          {/* Subtle Draft Grid Overlay */}
          <DraftGrid className="opacity-[0.06] text-white" />
          
          {/* Large Faint Horse Line Art Watermark */}
          <HorseLineArt className="absolute right-0 bottom-0 w-[50%] h-[50%] text-white/[0.05] pointer-events-none select-none hidden lg:block" />
        </div>

        <div className="mx-auto w-full max-w-7xl px-6 md:px-12 relative z-10 flex justify-start">
          {/* Left-aligned Content Card */}
          <div className="relative w-full lg:max-w-2xl bg-[#FCF9F5]/95 backdrop-blur-md p-8 md:p-12 lg:p-16 border border-stone-200 shadow-sm space-y-8 translate-y-36 lg:translate-y-52 text-center mx-auto lg:mx-0">
            {/* Corner Brackets for Editorial Blueprint Look */}
            <CornerBrackets className="text-stone-400" size="w-6 h-6" />
            
            {t.home.hero_badge && (
              <div className="inline-flex items-center gap-2 sm:gap-3 border border-stone-300 bg-stone-100 px-2.5 py-1 sm:px-4 sm:py-1.5 text-[8px] sm:text-[10px] uppercase tracking-[0.2em] sm:tracking-[0.3em] text-stone-800 rounded-none font-medium font-sans-custom mx-auto">
                <Shield className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-stone-700" />
                <span>{t.home.hero_badge}</span>
              </div>
            )}

            <h1 className="font-display text-3xl sm:text-6xl md:text-7xl leading-none uppercase text-stone-950">
              {t.home.hero_title_italic && (
                <span className="block italic text-stone-600 font-light lowercase font-display normal-case mb-2">{t.home.hero_title_italic}</span>
              )}
              <span className="block tracking-normal sm:tracking-editorial font-medium">{t.home.hero_title_main}</span>
            </h1>

            {t.home.hero_desc && (
              <p className="text-sm md:text-base leading-relaxed text-stone-600 font-light tracking-wide-custom font-sans-custom">
                {t.home.hero_desc}
              </p>
            )}

            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 pt-4">
              <Link
                to={lang === "da" ? "/da/heste/sportsheste" : "/en/horses/sport-horses"}
                className="inline-flex items-center justify-between gap-4 sm:gap-6 bg-stone-900 hover:bg-stone-950 text-stone-50 px-5 py-3 sm:px-8 sm:py-4 text-[10px] sm:text-xs font-semibold uppercase tracking-widest transition-colors duration-300 rounded-none cursor-pointer font-sans-custom"
              >
                <span>{t.home.hero_btn_collection}</span>
                <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              </Link>
              <Link
                to={lang === "da" ? "/da/kontakt" : "/en/contact"}
                className="inline-flex items-center gap-2 sm:gap-3 border border-stone-300 bg-white px-5 py-3 sm:px-8 sm:py-4 text-[10px] sm:text-xs font-semibold uppercase tracking-widest text-stone-700 hover:border-stone-800 hover:text-stone-900 transition-colors duration-300 rounded-none cursor-pointer font-sans-custom"
              >
                <span>{t.home.hero_btn_contact}</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3.5: Interactive Focus Boxes (Fokus Kasser) */}
      <section className="relative pt-48 pb-24 lg:pt-64 lg:pb-24 bg-stone-50 border-b border-stone-200 overflow-hidden">
        {/* Section Divider */}
        <div className="absolute top-0 left-0 right-0">
          <DecorativeDivider className="my-0 pt-8 max-w-7xl mx-auto opacity-60" />
        </div>

        <div className="mx-auto max-w-7xl px-6 md:px-12 relative z-10">
          <DecorativeDivider className="mb-12 max-w-2xl mx-auto" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Box 2: Uddannelse & Træning */}
            <Link
              to={lang === "da" ? "/da/om-os" : "/en/about-us"}
              className="group relative h-[550px] overflow-hidden border border-stone-200 shadow-sm flex flex-col justify-end p-8 transition-all duration-500 hover:shadow-md"
            >
              {/* Corner Brackets for Premium Blueprint Look */}
              <CornerBrackets className="text-white/30 group-hover:text-white/50 transition-colors duration-500 z-20" size="w-3.5 h-3.5" />

              <div className="absolute inset-0 z-0">
                <img
                  src="https://assets.auxodev.dk/assets/47342516-e670-4852-ad73-fdec3014e752/image00004_1.jpeg?width=600&quality=80&format=auto"
                  alt="Equinesport"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700 ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/40 to-transparent opacity-85 group-hover:opacity-90 transition-opacity duration-500" />
              </div>

              <div className="relative z-10 space-y-4">
                <h3 className="font-display text-xl sm:text-2xl text-white uppercase tracking-wide font-medium">
                  {t.home.box2_title.includes(" & ") ? (
                    <>
                      {t.home.box2_title.split(" & ").slice(0, -1).join(" & ")} & <span className="italic text-stone-300 lowercase font-display normal-case">{t.home.box2_title.split(" & ").slice(-1)}</span>
                    </>
                  ) : (
                    t.home.box2_title
                  )}
                </h3>
                <p className="text-xs text-stone-300 font-light leading-relaxed tracking-wide opacity-0 group-hover:opacity-100 max-h-0 group-hover:max-h-20 transition-all duration-500 ease-in-out overflow-hidden">
                  {t.home.box2_desc}
                </p>
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-white font-semibold pt-2 border-t border-white/20 w-full justify-between">
                  <span>{t.home.box2_btn}</span>
                  <ArrowRight className="h-3.5 w-3.5 transform group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </div>
            </Link>

            {/* Box 3: Referencer & Resultater */}
            <Link
              to={lang === "da" ? "/da/referencer" : "/en/references"}
              className="group relative h-[550px] overflow-hidden border border-stone-200 shadow-sm flex flex-col justify-end p-8 transition-all duration-500 hover:shadow-md"
            >
              {/* Corner Brackets for Premium Blueprint Look */}
              <CornerBrackets className="text-white/30 group-hover:text-white/50 transition-colors duration-500 z-20" size="w-3.5 h-3.5" />

              <div className="absolute inset-0 z-0">
                <img
                  src="https://assets.auxodev.dk/assets/47342516-e670-4852-ad73-fdec3014e752/image00003.jpeg?width=600&quality=80&format=auto"
                  alt="Referencer & Resultater"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700 ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/40 to-transparent opacity-85 group-hover:opacity-90 transition-opacity duration-500" />
              </div>

              <div className="relative z-10 space-y-4">
                <h3 className="font-display text-xl sm:text-2xl text-white uppercase tracking-wide font-medium">
                  {t.home.box3_title.split(" & ").slice(0, -1).join(" & ")} & <span className="italic text-stone-300 lowercase font-display normal-case">{t.home.box3_title.split(" & ").slice(-1)}</span>
                </h3>
                <p className="text-xs text-stone-300 font-light leading-relaxed tracking-wide opacity-0 group-hover:opacity-100 max-h-0 group-hover:max-h-20 transition-all duration-500 ease-in-out overflow-hidden">
                  {t.home.box3_desc}
                </p>
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-white font-semibold pt-2 border-t border-white/20 w-full justify-between">
                  <span>{t.home.box3_btn}</span>
                  <ArrowRight className="h-3.5 w-3.5 transform group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </div>
            </Link>

            {/* Box 4: Sponsorer & Partnere */}
            <Link
              to={lang === "da" ? "/da/sponsorer" : "/en/sponsors"}
              className="group relative h-[550px] overflow-hidden border border-stone-200 shadow-sm flex flex-col justify-end p-8 transition-all duration-500 hover:shadow-md"
            >
              {/* Corner Brackets for Premium Blueprint Look */}
              <CornerBrackets className="text-white/30 group-hover:text-white/50 transition-colors duration-500 z-20" size="w-3.5 h-3.5" />

              <div className="absolute inset-0 z-0">
                <img
                  src="https://assets.auxodev.dk/assets/47342516-e670-4852-ad73-fdec3014e752/10956b79-cda4-41cb-9b2d-c678d6d7ff4c.png?width=600&quality=80&format=auto"
                  alt="Sponsorer & Partnere"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700 ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/40 to-transparent opacity-85 group-hover:opacity-90 transition-opacity duration-500" />
              </div>

              <div className="relative z-10 space-y-4">
                <h3 className="font-display text-xl sm:text-2xl text-white uppercase tracking-wide font-medium">
                  {t.home.box4_title.split(" & ").slice(0, -1).join(" & ")} & <span className="italic text-stone-300 lowercase font-display normal-case">{t.home.box4_title.split(" & ").slice(-1)}</span>
                </h3>
                <p className="text-xs text-stone-300 font-light leading-relaxed tracking-wide opacity-0 group-hover:opacity-100 max-h-0 group-hover:max-h-20 transition-all duration-500 ease-in-out overflow-hidden">
                  {t.home.box4_desc}
                </p>
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-white font-semibold pt-2 border-t border-white/20 w-full justify-between">
                  <span>{t.home.box4_btn}</span>
                  <ArrowRight className="h-3.5 w-3.5 transform group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>



      {/* Section 6: Bottom Social & Community Banner */}
      <section className="relative py-16 md:py-24 lg:py-28 bg-stone-950 text-stone-100 overflow-hidden border-b border-stone-950">
        <div className="absolute inset-0 z-0 bg-stone-900 overflow-hidden">
          {heroImages.map((img, idx) => (
            <img
              key={img}
              src={`${img}?width=1600&quality=80&format=auto`}
              alt="Equinesport"
              className={`absolute inset-0 w-full h-full object-cover grayscale transition-opacity duration-1000 ease-in-out ${
                idx === currentHeroIdx ? "opacity-30" : "opacity-0"
              }`}
            />
          ))}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.03),transparent_80%)]" />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-950/95 via-stone-950/75 to-stone-950/95" />
          
          {/* Faint Draft Grid in Dark Mode */}
          <DraftGrid className="opacity-[0.04] text-white" />
        </div>

        <div className="mx-auto max-w-4xl px-6 text-center space-y-8 relative z-10">
          {/* Official Equinesport Brand Logo */}
          <div 
            className="relative mx-auto flex h-28 w-28 sm:h-36 sm:w-36 items-center justify-center bg-white p-1.5 shadow-2xl transition-transform duration-500 hover:scale-105"
            style={{ clipPath: "circle(49.5%)" }}
          >
            <img
              src="https://assets.auxodev.dk/assets/47342516-e670-4852-ad73-fdec3014e752/2e87f6a0-71a0-4e77-a6b8-93a81e3adb0a.png"
              alt="Equinesport Heineking Logo"
              className="h-full w-full object-cover"
              style={{ clipPath: "circle(49.5%)" }}
            />
          </div>

          {/* Social Media Links (enlarged as requested) */}
          <div className="space-y-4 pt-2">
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
              <a
                href={CONTACT_INFO.socials.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center border border-white/30 bg-white/10 text-white hover:bg-white hover:text-stone-950 hover:border-white transition-all duration-300 rounded-none shadow-xl backdrop-blur-md cursor-pointer"
                aria-label="Facebook"
              >
                <Facebook className="h-6 w-6 sm:h-7 sm:w-7 transition-transform duration-300 group-hover:scale-110" />
              </a>
              <a
                href={CONTACT_INFO.socials.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center border border-white/30 bg-white/10 text-white hover:bg-white hover:text-stone-950 hover:border-white transition-all duration-300 rounded-none shadow-xl backdrop-blur-md cursor-pointer"
                aria-label="Instagram"
              >
                <Instagram className="h-6 w-6 sm:h-7 sm:w-7 transition-transform duration-300 group-hover:scale-110" />
              </a>
              <a
                href={CONTACT_INFO.socials.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center border border-white/30 bg-white/10 text-white hover:bg-white hover:text-stone-950 hover:border-white transition-all duration-300 rounded-none shadow-xl backdrop-blur-md cursor-pointer"
                aria-label="YouTube"
              >
                <Youtube className="h-6 w-6 sm:h-7 sm:w-7 transition-transform duration-300 group-hover:scale-110" />
              </a>
            </div>
          </div>

          {/* Friendly Invitation Text */}
          <div className="pt-2 max-w-2xl mx-auto">
            <p className="font-serif italic text-lg sm:text-2xl md:text-3xl text-stone-200 tracking-wide leading-relaxed">
              {t.home.invitation_text}
            </p>
          </div>

          {/* Contact Button */}
          <div className="pt-4">
            <Link
              to={lang === "da" ? "/da/kontakt" : "/en/contact"}
              className="inline-flex items-center gap-3 border border-white/30 bg-white/10 hover:bg-white hover:text-stone-950 text-white px-6 py-3.5 sm:px-8 sm:py-4 text-[10px] sm:text-xs font-semibold uppercase tracking-widest transition-all duration-300 rounded-none backdrop-blur-md"
            >
              <span>{lang === "da" ? "Kontakt os" : "Contact us"}</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
